import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { AgencyProviderPackageModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/agencyProviderPackage/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/agencyProviderPackage/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/agencyProviderPackage/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/agencyProviderPackage/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/agencyProviderPackage/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/agencyProviderPackage/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/agencyProviderPackage/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/agencyProviderPackage/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/agencyProviderPackage/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/agencyProviderPackage/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/agencyProviderPackage/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/agencyProviderPackage/DELETE_FAILURE'

export type AgencyProviderPackageModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: AgencyProviderPackageModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}
// Action Creators
export function loadAgencyProviderPackages(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().agencyProviderPackages.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/agency_provider_packages.json', options)
      .then(({ data }: { data: AgencyProviderPackageModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PROVIDER_PACKAGE_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createAgencyProviderPackage(
  agencyProviderPackage: Partial<AgencyProviderPackageModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      agency_provider_package: agencyProviderPackage,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/agency_provider_packages.json', options, config)
      .then(({ data }: { data: AgencyProviderPackageModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PROVIDER_PACKAGE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkCreateAgencyProviderPackages(
  params: { agency_id?: number, agency_ids?: number[], provider_package_id?: number, provider_package_ids?: number[] },
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify(params),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/agency_provider_packages/bulk_create.json', options, config)
      .then(({ data }: { data: AgencyProviderPackageModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PROVIDER_PACKAGE_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteAgencyProviderPackage(agencyProviderPackage: DeleteParams<AgencyProviderPackageModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/agency_provider_packages/${agencyProviderPackage.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PROVIDER_PACKAGE)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

type BulkDeleteParam = {
  agency_id?: number,
  agency_provider_package_ids?: number[],
  provider_package_id?: number,
}

export function bulkDeleteAgencyProviderPackages(params: BulkDeleteParam, options: EntityOptions = {}) {
  const {
    agency_id, agency_provider_package_ids, provider_package_id,
  } = params

  const config = {
    method: 'DELETE',
    data: JSON.stringify({
      agency_id,
      agency_provider_package_ids,
      provider_package_id,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api('/agency_provider_packages/bulk_delete.json', options, config)
      .then(({ data }: { data: { ids: number[] } }) => {
        if (data.ids){
          data.ids.forEach((id) => {
            const normalizedJson = normalize({ id }, Schemas.AGENCY_PROVIDER_PACKAGE)
            // @ts-expect-error TS2345
            dispatch(deleteEntity(normalizedJson))
          })
        }

        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function toggleVisibility(agencyProviderPackage: AgencyProviderPackageModel) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agency_provider_packages/${agencyProviderPackage.id}/toggle_visibility.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PROVIDER_PACKAGE)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: AgencyProviderPackageModuleState = initialState,
  action: Action = { type: '' },
): AgencyProviderPackageModuleState {
  switch (action.type) {
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
